import { defineStore } from 'pinia';
import { computed, readonly, shallowRef } from 'vue';

export type VideoUploadJobStatus = 'queued' | 'uploading' | 'processing' | 'complete' | 'error' | 'cancelled';

export type VideoUploadJobUploaderType = 'create' | 'edit';

export type VideoUploadJobFileStatus =
    | 'queued'
    | 'uploading'
    | 'uploaded'
    | 'imported'
    | 'processing'
    | 'complete'
    | 'error'
    | 'cancelled';

export interface StartVideoUploadJobFilePayload {
    id: string;
    name: string;
    size: number;
    isImported: boolean;
}

export interface StartVideoUploadJobPayload {
    eventId: number;
    eventExternalId: string | null;
    eventTitle: string;
    serviceSlug: string | null;
    uploaderType: VideoUploadJobUploaderType;
    totalFiles: number;
    totalBytes: number;
    files: StartVideoUploadJobFilePayload[];
}

export interface VideoUploadJobFile {
    id: string;
    name: string;
    index: number;
    size: number;
    uploadedBytes: number;
    status: VideoUploadJobFileStatus;
    isImported: boolean;
}

export interface VideoUploadJob {
    id: string;
    eventId: number;
    eventExternalId: string | null;
    eventTitle: string;
    serviceSlug: string | null;
    uploaderType: VideoUploadJobUploaderType;
    status: VideoUploadJobStatus;
    currentFileIndex: number;
    currentFileName: string | null;
    totalFiles: number;
    uploadedBytes: number;
    totalBytes: number;
    files: VideoUploadJobFile[];
    errorMessage: string | null;
    createdAt: string;
    updatedAt: string;
}

const terminalStatuses = new Set<VideoUploadJobStatus>(['complete', 'error', 'cancelled']);

export const useVideoUploadQueue = defineStore('videoUploadQueue', () => {
    const activeJob = shallowRef<VideoUploadJob | null>(null);

    const hasActiveJob = computed(() => activeJob.value !== null);
    const isUploading = computed(() => activeJob.value?.status === 'uploading');
    const isProcessing = computed(() => activeJob.value?.status === 'processing');
    const isTerminal = computed(() => {
        const status = activeJob.value?.status;

        return status ? terminalStatuses.has(status) : false;
    });

    const hasRunningJob = computed(() => {
        const status = activeJob.value?.status;
        return status ? !terminalStatuses.has(status) : false;
    });
    const canStartJob = computed(() => !hasRunningJob.value);
    const canCancelActiveJob = computed(() => {
        const status = activeJob.value?.status;
        return status === 'queued' || status === 'uploading';
    });

    const activeJobPercent = computed(() => {
        const job = activeJob.value;

        if (!job) {
            return 0;
        }

        if (job.status === 'complete') {
            return 100;
        }

        if (job.totalBytes <= 0) {
            return 0;
        }

        return Math.min(100, Math.round((job.uploadedBytes / job.totalBytes) * 100));
    });

    function startJob(payload: StartVideoUploadJobPayload): VideoUploadJob {
        if (activeJob.value && !terminalStatuses.has(activeJob.value.status)) {
            throw new Error('A video upload job is already active');
        }

        const now = new Date().toISOString();
        const job: VideoUploadJob = {
            id: createJobId(),
            eventId: payload.eventId,
            eventExternalId: payload.eventExternalId,
            eventTitle: payload.eventTitle,
            serviceSlug: payload.serviceSlug,
            uploaderType: payload.uploaderType,
            status: 'queued',
            currentFileIndex: 0,
            currentFileName: null,
            totalFiles: payload.totalFiles,
            uploadedBytes: 0,
            totalBytes: payload.totalBytes,
            files: payload.files.map((file, index) => ({
                id: file.id,
                name: file.name,
                index,
                size: file.size,
                uploadedBytes: file.isImported ? file.size : 0,
                status: file.isImported ? 'imported' : 'queued',
                isImported: file.isImported,
            })),
            errorMessage: null,
            createdAt: now,
            updatedAt: now,
        };

        activeJob.value = job;

        return job;
    }

    function markUploading(jobId: string): void {
        updateJob(jobId, { status: 'uploading', errorMessage: null });
    }

    function updateProgress(jobId: string, uploadedBytes: number): void {
        const job = requireActiveJob(jobId);
        const safeUploadedBytes = Math.max(0, Math.min(uploadedBytes, job.totalBytes));

        updateJob(jobId, { uploadedBytes: safeUploadedBytes });
    }

    function addUploadedBytes(jobId: string, bytes: number): void {
        const job = requireActiveJob(jobId);

        updateProgress(jobId, job.uploadedBytes + bytes);
    }

    function addFileUploadedBytes(jobId: string, fileId: string, bytes: number): void {
        const job = requireActiveJob(jobId);
        const files = job.files.map(file => {
            if (file.id !== fileId) {
                return file;
            }

            const safeUploadedBytes = Math.max(0, Math.min(file.uploadedBytes + bytes, file.size));

            return {
                ...file,
                uploadedBytes: safeUploadedBytes,
            };
        });

        updateJob(jobId, { files });
    }

    function updateCurrentFile(jobId: string, currentFileIndex: number, currentFileName?: string): void {
        const job = requireActiveJob(jobId);
        const safeCurrentFileIndex = Math.max(0, Math.min(currentFileIndex, job.totalFiles));

        updateJob(jobId, {
            currentFileIndex: safeCurrentFileIndex,
            currentFileName: currentFileName ?? job.currentFileName,
        });
    }

    function updateFileStatus(jobId: string, fileId: string, status: VideoUploadJobFileStatus): void {
        const job = requireActiveJob(jobId);
        const files = job.files.map(file => {
            if (file.id !== fileId) {
                return file;
            }

            return {
                ...file,
                status,
                uploadedBytes: status === 'uploaded' || status === 'imported' ? file.size : file.uploadedBytes,
            };
        });

        updateJob(jobId, { files });
    }

    function markProcessing(jobId: string): void {
        const job = requireActiveJob(jobId);
        const files: VideoUploadJobFile[] = job.files.map(file => ({
            ...file,
            status: file.status === 'queued' || file.status === 'uploading' ? 'processing' : file.status,
        }));

        updateJob(jobId, { status: 'processing', files });
    }

    function markComplete(jobId: string): void {
        const job = requireActiveJob(jobId);
        const files: VideoUploadJobFile[] = job.files.map(file => ({
            ...file,
            status: file.isImported ? 'imported' : 'complete',
            uploadedBytes: file.size,
        }));

        updateJob(jobId, {
            status: 'complete',
            currentFileIndex: job.totalFiles,
            currentFileName: null,
            uploadedBytes: job.totalBytes,
            files,
            errorMessage: null,
        });
    }

    function markError(jobId: string, message: string): void {
        const job = requireActiveJob(jobId);
        const files: VideoUploadJobFile[] = job.files.map(file => ({
            ...file,
            status: file.status === 'uploading' || file.status === 'queued' ? 'error' : file.status,
        }));

        updateJob(jobId, {
            status: 'error',
            errorMessage: message,
            files,
        });
    }

    function markCancelled(jobId: string): void {
        const job = requireActiveJob(jobId);
        const files: VideoUploadJobFile[] = job.files.map(file => ({
            ...file,
            status: file.status === 'uploading' || file.status === 'queued' ? 'cancelled' : file.status,
        }));

        updateJob(jobId, {
            status: 'cancelled',
            files,
        });
    }

    function dismissJob(jobId: string): void {
        const job = activeJob.value;
        if (!job || job?.id !== jobId) {
            return;
        }

        if (!terminalStatuses.has(job.status)) {
            return;
        }

        activeJob.value = null;
    }

    function updateJob(jobId: string, patch: Partial<Omit<VideoUploadJob, 'id' | 'createdAt'>>): void {
        const job = requireActiveJob(jobId);

        activeJob.value = {
            ...job,
            ...patch,
            updatedAt: new Date().toISOString(),
        };
    }

    function requireActiveJob(jobId: string): VideoUploadJob {
        const job = activeJob.value;

        if (!job || job.id !== jobId) {
            throw new Error('Video upload job not found');
        }

        return job;
    }

    function createJobId(): string {
        return crypto.randomUUID?.() ?? `video-upload-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    return {
        activeJob: readonly(activeJob),
        hasActiveJob,
        hasRunningJob,
        canStartJob,
        canCancelActiveJob,
        isUploading,
        isProcessing,
        isTerminal,
        activeJobPercent,
        startJob,
        markUploading,
        updateProgress,
        addUploadedBytes,
        addFileUploadedBytes,
        updateCurrentFile,
        updateFileStatus,
        markProcessing,
        markComplete,
        markError,
        markCancelled,
        dismissJob,
    };
});
